import { Link } from "react-router-dom";
import { ArrowRight, Buildings, Plant, Briefcase, ShieldCheck, ChartLineUp, Handshake } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { OpportunityCard } from "@/components/Cards";
import PageSEO from "@/components/PageSEO";

const FOCUS = [
  { icon: Buildings, title: "REAL ESTATE",
    desc: "Exploring opportunities within property development, land-related projects, and carefully selected real estate ventures with potential for sustainable value creation.",
    cta: "Explore Real Estate", to: "/real-estate" },
  { icon: Plant, title: "AGRICULTURE",
    desc: "Supporting opportunities across farming, agricultural production, and the wider agricultural value chain with a focus on sustainable and commercially viable ventures.",
    cta: "Explore Agriculture", to: "/agriculture" },
  { icon: Briefcase, title: "STRATEGIC BUSINESS INVESTMENTS",
    desc: "Exploring carefully selected businesses and projects with growth potential, strong fundamentals, and the ability to create sustainable economic value.",
    cta: "Explore Opportunities", to: "/strategic" },
];

const PRINCIPLES = [
  { icon: ShieldCheck, title: "Transparent by Design", body: "Every opportunity ships with published terms, risk information and clear duration — no unrealistic promises." },
  { icon: ChartLineUp, title: "Structured for Growth", body: "Sector-focused vehicles across real estate, agriculture and selected businesses — built for long-term value." },
  { icon: Handshake, title: "Community-First", body: "From ₦2,000 investment circles to institutional partnerships — investing that belongs to more Nigerians." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    http.get("/opportunities").then(r => setFeatured(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <PageSEO
        title="Invest in Real Estate, Agriculture & Strategic Businesses in Nigeria"
        description="Mayoto is a Nigerian investment company creating structured opportunities across Real Estate, Agriculture and Strategic Business Investments. Invest smart. Grow together."
        canonicalPath="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden mayo-grain">
        <div className="container-wide pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="inline-flex items-center gap-2 text-xs font-display font-bold tracking-[0.25em] text-[#0a1128] bg-white border border-slate-200 rounded-full px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-[#00b4d8]" /> A NIGERIAN INVESTMENT COMPANY
            </p>
            <h1 className="mt-6 font-display font-black text-[2.75rem] sm:text-6xl lg:text-7xl leading-[0.95] text-[#0a1128] tracking-tight" data-testid="hero-title">
              Building Opportunities.<br/>
              <span className="text-[#00b4d8]">Growing</span> <span className="relative"><span className="gold-underline">Wealth.</span></span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Mayoto creates structured investment opportunities across Real Estate, Agriculture, and strategic businesses — connecting investors with opportunities designed to create long-term value.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/investments" data-testid="hero-cta-primary">
                <Button className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7] text-white rounded-full px-7 py-6 text-base">
                  Explore Investment Opportunities <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/partnerships" data-testid="hero-cta-secondary">
                <Button variant="outline" className="rounded-full px-7 py-6 text-base border-[#0a1128] text-[#0a1128] hover:bg-[#0a1128] hover:text-white">
                  Partner With Mayoto
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
              <div>
                <p className="stat-number text-3xl text-[#0a1128]">3</p>
                <p className="text-xs text-slate-500 mt-1">Investment Sectors</p>
              </div>
              <div>
                <p className="stat-number text-3xl text-[#0a1128]">₦2K</p>
                <p className="text-xs text-slate-500 mt-1">Minimum entry</p>
              </div>
              <div>
                <p className="stat-number text-3xl text-[#0a1128]">100%</p>
                <p className="text-xs text-slate-500 mt-1">Transparent Terms</p>
              </div>
            </div>
          </div>

          {/* Hero visual — bento composition */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[520px]">
              <div className="col-span-4 row-span-4 rounded-2xl overflow-hidden shadow-2xl relative">
                <img src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhZnJpY2FuJTIwcmVhbCUyMGVzdGF0ZSUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODU1Mzk4NDJ8MA&ixlib=rb-4.1.0&q=85" alt="Modern African real estate" className="h-full w-full object-cover" />
                <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-xl px-3 py-2">
                  <p className="text-xs text-slate-500">SECTOR</p>
                  <p className="font-display font-bold text-[#0a1128] text-sm">Real Estate</p>
                </div>
              </div>
              <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden">
                <img src="https://images.pexels.com/photos/9374827/pexels-photo-9374827.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Agriculture field" className="h-full w-full object-cover" />
              </div>
              <div className="col-span-2 row-span-3 rounded-2xl overflow-hidden bg-[#0a1128] text-white p-4 flex flex-col justify-between">
                <div className="h-8 w-8 rounded-lg bg-[#00b4d8] grid place-items-center"><ChartLineUp size={18} weight="bold" /></div>
                <div>
                  <p className="text-xs text-slate-400">PORTFOLIO GROWTH</p>
                  <p className="font-display font-black text-3xl mt-1">+24%*</p>
                  <p className="text-[10px] text-slate-500 mt-1">*Illustrative — not a guarantee</p>
                </div>
              </div>
              <div className="col-span-4 row-span-2 rounded-2xl overflow-hidden">
                <img src="https://images.unsplash.com/photo-1562910859-be83f1df7b56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxuaWdlcmlhbiUyMGVudHJlcHJlbmV1cnMlMjBtZWV0aW5nfGVufDB8fHx8MTc4NTUzOTg0Mnww&ixlib=rb-4.1.0&q=85" alt="Nigerian entrepreneurs" className="h-full w-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        {/* Tape */}
        <div className="tape py-4 overflow-hidden border-y border-white/10">
          <div className="container-wide flex items-center justify-center text-sm sm:text-base">
            <span>INVEST SMART. GROW TOGETHER.</span>
            <span className="mx-6 opacity-40">✦</span>
            <span className="hidden sm:inline">REAL ESTATE</span>
            <span className="mx-6 opacity-40 hidden sm:inline">✦</span>
            <span className="hidden sm:inline">AGRICULTURE</span>
            <span className="mx-6 opacity-40 hidden sm:inline">✦</span>
            <span className="hidden sm:inline">STRATEGIC INVESTMENTS</span>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-wide">
          <Reveal>
            <SectionHeader
              eyebrow="WHY MAYOTO"
              title="Serious investing, built for a new generation of Nigerians."
              description="We think about wealth creation the same way we think about our reputation — with discipline, transparency and long-term thinking."
            />
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div className="card-lift h-full bg-white border border-slate-200 rounded-2xl p-8">
                  <div className="h-12 w-12 rounded-xl bg-[#0a1128] text-[#00b4d8] grid place-items-center">
                    <p.icon size={22} weight="bold" />
                  </div>
                  <h3 className="mt-6 font-display font-bold text-xl text-[#0a1128]">{p.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT FOCUS */}
      <section className="py-24 lg:py-32 bg-[#f8fafc]">
        <div className="container-wide">
          <Reveal><SectionHeader eyebrow="OUR INVESTMENT FOCUS" title="Three sectors. One disciplined approach." /></Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {FOCUS.map((f, i) => (
              <Reveal key={f.title} delay={i * 100}>
                <div className="card-lift h-full bg-white border border-slate-200 rounded-2xl p-8 flex flex-col">
                  <div className="h-14 w-14 rounded-2xl bg-[#00b4d8]/10 text-[#00b4d8] grid place-items-center">
                    <f.icon size={26} weight="bold" />
                  </div>
                  <h3 className="mt-6 font-display font-black text-2xl text-[#0a1128] tracking-tight leading-tight">{f.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed flex-1">{f.desc}</p>
                  <Link to={f.to} className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-[#00b4d8] hover:text-[#0096c7]" data-testid={`focus-cta-${f.title.replace(/\s+/g,'-').toLowerCase()}`}>
                    {f.cta} <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED OPPORTUNITIES */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-wide">
          <Reveal>
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
              <SectionHeader eyebrow="FEATURED OPPORTUNITIES" title="Now open on Mayoto." description="A selection of currently open opportunities across our three sectors." />
              <Link to="/investments" className="text-sm font-semibold text-[#00b4d8] hover:text-[#0096c7] inline-flex items-center gap-1" data-testid="featured-see-all">
                See all <ArrowRight size={14} />
              </Link>
            </div>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((o) => <OpportunityCard key={o.id} opp={o} />)}
            {featured.length === 0 && (
              <div className="col-span-full text-slate-500 text-center py-12">Loading opportunities…</div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32">
        <div className="container-wide">
          <div className="rounded-3xl overflow-hidden mayo-navy-bg relative">
            <div className="absolute inset-0 opacity-30">
              <img src="https://images.unsplash.com/photo-1651313949559-d1cd72300a19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwY29ycG9yYXRlJTIwZ2FsYSUyMGV2ZW50fGVufDB8fHx8MTc4NTUzOTg0Mnww&ixlib=rb-4.1.0&q=85" alt="Mayoto Community" className="h-full w-full object-cover" />
            </div>
            <div className="relative p-10 sm:p-16 lg:p-20">
              <p className="text-[#d4af37] font-display font-bold tracking-[0.25em] text-xs">JOIN THE COMMUNITY</p>
              <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-2xl">Ready to invest smart?</h2>
              <p className="mt-5 text-slate-300 max-w-xl">Create your investor account in minutes. Explore opportunities across three sectors, review the terms transparently and decide with confidence.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/register" data-testid="home-cta-register">
                  <Button className="btn-shine bg-[#00b4d8] hover:bg-[#0096c7] rounded-full px-7 py-6 text-base">Create Investor Account</Button>
                </Link>
                <Link to="/how-it-works" data-testid="home-cta-how">
                  <Button variant="outline" className="rounded-full px-7 py-6 text-base border-white/40 text-white hover:bg-white/10">How It Works</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
